// Public landing page served at the product-domain apex (vista-cloud.io).
//
// Users who bookmark or type the bare product domain land here instead
// of the normal SPA auth flow. The apex is not behind CF Access, so
// running the auth flow would loop against api.vista-cloud.io's 401.
// This page short-circuits that: ask for the org slug, look it up via
// the public /api/v1/orgs/lookup endpoint (server-authoritative redirect
// URL in the response — see security notes below), then redirect the
// browser to the org's subdomain, which IS behind CF Access.
//
// Security posture:
//
// 1. Redirect URL comes from the server's response (`body.redirect_url`),
//    NOT from client-side URL construction using user input. The user
//    types `foo`; we send `foo` to the server; the server either says
//    `{exists: false}` or `{exists: true, redirect_url: "https://foo.vista-cloud.io/"}`.
//    We only follow `redirect_url`. Eliminates the open-redirect class
//    of bugs you'd get from `` `https://${input}.vista-cloud.io` ``.
//
// 2. Client-side slug validation mirrors the server's regex for UX
//    (fail fast, no network round-trip on obviously-bad input). It is
//    NOT a security boundary — the server re-validates.
//
// 3. Errors are rendered via React's default escaping (`{error}`,
//    never `dangerouslySetInnerHTML`). The server also does not echo
//    the user's input on validation failure (see routes/orgs.js),
//    belt-and-suspenders.
//
// 4. `window.location.href` is the only sink, and it's only ever
//    assigned the server-returned `redirect_url`. No click handlers
//    or `href` attributes built from user input.
//
// 5. Rate-limit errors surface as a polite "try again in a minute"
//    so a scraper doesn't get useful error detail.

import { useState } from 'react';

const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

function getApiBase() {
  const explicit = import.meta.env.VITE_API_BASE;
  if (explicit) return explicit.replace(/\/$/, '');
  // Default to same-origin api subdomain. The apex is vista-cloud.io;
  // the API is at api.vista-cloud.io. In the unlikely event this
  // component is rendered elsewhere, VITE_API_BASE can override.
  return `${window.location.protocol}//api.${window.location.hostname}`;
}

export default function LandingPage() {
  const [slug, setSlug] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    // Canonicalize — trim + lowercase. Server does the same; client
    // mirror is for UX only.
    const canonical = slug.trim().toLowerCase();
    if (!canonical) {
      setError('Enter your organization slug.');
      return;
    }
    if (!SLUG_REGEX.test(canonical)) {
      setError('Organization slug can only contain letters, numbers, and hyphens.');
      return;
    }

    setSubmitting(true);
    try {
      const apiBase = getApiBase();
      const resp = await fetch(
        `${apiBase}/api/v1/orgs/lookup?slug=${encodeURIComponent(canonical)}`,
        // No credentials — this endpoint is public.
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );

      if (resp.status === 429) {
        setError('Too many lookups. Please try again in a minute.');
        return;
      }
      if (!resp.ok) {
        // 400 / 500 / anything else: generic. Don't leak server errors.
        setError('Could not look up organization. Please try again.');
        return;
      }

      // `{ data, cursor, error }` envelope (vista#670) — the lookup payload
      // is under `data`. Read through one accessor so the guard below and the
      // redirect cannot end up reading different levels.
      const body = (await resp.json())?.data ?? {};
      if (!body.exists || !body.redirect_url) {
        setError('Organization not found. Check the slug with your administrator.');
        return;
      }

      // Final safety check: redirect_url MUST be an https:// URL ending
      // at the product domain. If the server is compromised or the
      // response is tampered with in transit (unlikely over TLS, but
      // belt-and-suspenders), refuse to follow an off-platform redirect.
      if (!isValidRedirect(body.redirect_url)) {
        setError('Unexpected response. Please try again or contact support.');
        return;
      }

      window.location.href = body.redirect_url;
    } catch {
      // Network error — do not expose error details to the user.
      setError('Could not reach the lookup service. Check your connection.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">VISTA Cloud</h1>
          <p className="text-sm text-gray-600 mb-6">
            Enter your organization to sign in.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            <label htmlFor="org-slug" className="block text-sm font-medium text-gray-700 mb-1">
              Organization
            </label>
            <div className="flex items-stretch rounded-md border border-gray-300 overflow-hidden focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 mb-3">
              <input
                id="org-slug"
                name="org-slug"
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                autoFocus
                disabled={submitting}
                placeholder="your-org"
                autoComplete="organization"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                maxLength={63}
                className="flex-1 px-3 py-2 text-sm outline-none disabled:bg-gray-50"
              />
              <span className="px-3 py-2 text-sm text-gray-500 bg-gray-50 border-l border-gray-300 select-none">
                .vista-cloud.io
              </span>
            </div>

            {error && (
              <p role="alert" className="text-sm text-red-600 mb-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || !slug.trim()}
              className="w-full bg-blue-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Looking up…' : 'Continue'}
            </button>
          </form>

          <p className="text-xs text-gray-500 mt-6 text-center">
            Don't know your organization slug? Contact your administrator.
          </p>
        </div>

        <p className="text-xs text-gray-400 mt-4 text-center">
          Colorado Communications
        </p>
      </div>
    </div>
  );
}

// Exported for unit testing the redirect sanity check in isolation.
export function isValidRedirect(url, productDomain = 'vista-cloud.io') {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return false;
    // Host must be `{slug}.vista-cloud.io` — exactly one label before
    // the product domain. Rejects both apex and nested subdomains.
    if (u.hostname === productDomain) return false;
    if (!u.hostname.endsWith(`.${productDomain}`)) return false;
    const leading = u.hostname.slice(0, -(productDomain.length + 1));
    if (!leading || leading.includes('.')) return false;
    return true;
  } catch {
    return false;
  }
}
